import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlayerInvitations, acceptInvitation, declineInvitation } from '../services/clubs';
import { PlayerInvitationDetails } from '../services/clubs';
import Loading from '../components/Loading';
import './Invitations.css';

const Invitations: React.FC = () => {
  const { t } = useTranslation();
  const [invitations, setInvitations] = useState<PlayerInvitationDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<number | null>(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPlayerInvitations();
      setInvitations(data);
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: number) => {
    setProcessing(invitationId);
    setError(null);
    try {
      await acceptInvitation(invitationId);
      await loadInvitations(); // Reload to update the list
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeclineInvitation = async (invitationId: number) => {
    setProcessing(invitationId);
    setError(null);
    try {
      await declineInvitation(invitationId);
      await loadInvitations(); // Reload to update the list
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'status-badge pending';
      case 'accepted': return 'status-badge accepted';
      case 'declined': return 'status-badge declined';
      case 'expired': return 'status-badge expired';
      default: return 'status-badge';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return t('invitations.status.pending');
      case 'accepted': return t('invitations.status.accepted');
      case 'declined': return t('invitations.status.declined');
      case 'expired': return t('invitations.status.expired');
      default: return status;
    }
  };

  const isInvitationExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) return <Loading />;

  return (
    <div className="invitations-container">
      <div className="invitations-header">
        <h1>{t('invitations.title')}</h1>
        <p>{t('invitations.description')}</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {invitations.length === 0 ? (
        <div className="no-invitations">
          <div className="no-invitations-icon">📬</div>
          <h3>{t('invitations.noInvitations')}</h3>
          <p>{t('invitations.noInvitationsDescription')}</p>
        </div>
      ) : (
        <div className="invitations-list">
          {invitations.map((invitation) => (
            <div key={invitation.id} className="invitation-card">
              <div className="invitation-header">
                <div className="invitation-club-info">
                  <h3 className="club-name">{invitation.club_name}</h3>
                  {invitation.club_description && (
                    <p className="club-description">{invitation.club_description}</p>
                  )}
                </div>
                <div className={`status-badge ${getStatusBadgeClass(invitation.status)}`}>
                  {getStatusLabel(invitation.status)}
                </div>
              </div>

              <div className="invitation-details">
                <div className="invitation-info">
                  <p><strong>{t('invitations.invitedBy')}:</strong> {invitation.invited_by_name}</p>
                  <p><strong>{t('invitations.role')}:</strong> {t(`clubs.roles.${invitation.role}`)}</p>
                  <p><strong>{t('invitations.invitedOn')}:</strong> {formatDateTime(invitation.created_at)}</p>
                  {invitation.status === 'pending' && (
                    <p><strong>{t('invitations.expiresOn')}:</strong> {formatDateTime(invitation.expires_at)}</p>
                  )}
                </div>

                {invitation.message && (
                  <div className="invitation-message">
                    <strong>{t('invitations.message')}:</strong>
                    <p>{invitation.message}</p>
                  </div>
                )}
              </div>

              {invitation.status === 'pending' && !isInvitationExpired(invitation.expires_at) && (
                <div className="invitation-actions">
                  <button
                    onClick={() => handleAcceptInvitation(invitation.id)}
                    disabled={processing === invitation.id}
                    className="btn btn-success"
                  >
                    {processing === invitation.id ? t('invitations.accepting') : t('invitations.accept')}
                  </button>
                  <button
                    onClick={() => handleDeclineInvitation(invitation.id)}
                    disabled={processing === invitation.id}
                    className="btn btn-danger"
                  >
                    {processing === invitation.id ? t('invitations.declining') : t('invitations.decline')}
                  </button>
                </div>
              )}

              {invitation.status === 'pending' && isInvitationExpired(invitation.expires_at) && (
                <div className="invitation-expired">
                  <p>{t('invitations.invitationExpired')}</p>
                </div>
              )}

              {invitation.status === 'accepted' && (
                <div className="invitation-accepted">
                  <p>{t('invitations.invitationAccepted')}</p>
                </div>
              )}

              {invitation.status === 'declined' && (
                <div className="invitation-declined">
                  <p>{t('invitations.invitationDeclined')}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Invitations;
